import {
  useState,
  useCallback,
  useRef,
  useContext,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getGiveawayAssets, GiveawayResponse } from '@org-avp/avp-hck-dga-customerui-commons';
import { GiveawayContext } from '@/state/state';
import { Size } from '@volkswagen-onehub/icons-core';
import styled from 'styled-components';
import {
  Breakpoints,
  Text,
  TextTag,
  TokenTextAppearance,
  TextAlignment,
} from '@volkswagen-onehub/components-core';
import { minimalScreenWidth } from '@/common/breakpoints';

const MOBILE_COMPARISON_SLIDER_BREAKPOINT = Breakpoints.b960;
const MEDIUM_COMPARISON_SLIDER_SIDE_SPACE = 'clamp(48px, 7.5vw, 160px)';

const MIN_SLIDER_POSITION = 10;
const MAX_SLIDER_POSITION = 90;
const SLIDER_KEYBOARD_STEP = 5;

const clampSliderPosition = (position: number): number => {
  return Math.max(MIN_SLIDER_POSITION, Math.min(MAX_SLIDER_POSITION, position));
};

export const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  cursor: col-resize;
  aspect-ratio: 16 / 9;
  border-radius: 0;
  touch-action: pan-y;
  overscroll-behavior-x: contain;
  user-select: none;
  -webkit-user-select: none;

  @media (min-width: ${MOBILE_COMPARISON_SLIDER_BREAKPOINT}px) {
    border-radius: 16px;
  }
`;

export const SliderImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  display: block;
  user-select: none;
  -webkit-user-select: none;
  -webkit-user-drag: none;
`;

export const BeforeImageClip = styled.div<{ $position: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  clip-path: ${(props) => `inset(0 ${100 - props.$position}% 0 0)`};
  z-index: 2;
`;

export const HandleLine = styled.div<{ $position: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.$position}%;
  transform: translateX(-50%);
  width: 2px;
  background: white;
  z-index: 3;
  pointer-events: none;
`;

export const HandleKnob = styled.div<{ $position: number }>`
  position: absolute;
  bottom: 10%;
  left: ${(props) => props.$position}%;
  transform: translateX(-50%);
  z-index: 4;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${(props) => props.theme.colors.background.secondary};
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: ${(props) => props.theme.colors.shadow.variant1};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;

  &:hover {
    transform: translateX(-50%) scale(1.1);
  }
`;

export const StyledSliderWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: ${(props) => props.theme.size.dynamic0100};

  @media (min-width: ${MOBILE_COMPARISON_SLIDER_BREAKPOINT}px) and (max-width: ${Breakpoints.b1920 -
    1}px) {
    padding-left: ${MEDIUM_COMPARISON_SLIDER_SIDE_SPACE};
    padding-right: ${MEDIUM_COMPARISON_SLIDER_SIDE_SPACE};
  }

  @media (min-width: ${Breakpoints.b1920}px) {
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: ${MOBILE_COMPARISON_SLIDER_BREAKPOINT - 1}px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

export const StyledSubTitleWrapper = styled(Text)`
  padding-top: ${(props) => props.theme.size.dynamic0350};
  padding-bottom: ${(props) => props.theme.size.dynamic0130};

  @media screen and ${minimalScreenWidth.m} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }

  @media screen and ${minimalScreenWidth.l} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }

  @media screen and ${minimalScreenWidth.xxl} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }
`;

export function ImageComparisonSlider() {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const giveawayData: GiveawayResponse = useContext(GiveawayContext);
  const { t } = useTranslation();

  const assetDay = getGiveawayAssets(giveawayData, 'compare-slider')[0];
  const assetNight = getGiveawayAssets(giveawayData, 'compare-slider-night')[0];

  const getPointerPositionPercent = useCallback((clientX: number): number | null => {
    const container = containerRef.current;

    if (!container) {
      return null;
    }

    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;

    return (x / rect.width) * 100;
  }, []);

  const updatePosition = useCallback(
    (clientX: number) => {
      const percent = getPointerPositionPercent(clientX);

      if (percent === null) {
        return;
      }

      setPosition(clampSliderPosition(percent));
    },
    [getPointerPositionPercent],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const percent = getPointerPositionPercent(e.clientX);

      if (percent === null || percent < MIN_SLIDER_POSITION || percent > MAX_SLIDER_POSITION) {
        return;
      }

      isDragging.current = true;

      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);

      setPosition(clampSliderPosition(percent));
    },
    [getPointerPositionPercent],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!isDragging.current) {
        return;
      }

      e.preventDefault();
      updatePosition(e.clientX);
    },
    [updatePosition],
  );

  const handlePointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const handlePointerCancel = useCallback((e: PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const handleKnobKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setPosition((prev) => clampSliderPosition(prev - SLIDER_KEYBOARD_STEP));
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setPosition((prev) => clampSliderPosition(prev + SLIDER_KEYBOARD_STEP));
    }
  }, []);

  if (!assetDay || !assetNight) {
    return null;
  }

  return (
    <StyledSliderWrapper>
      <StyledSubTitleWrapper
        tag={TextTag.h2}
        textAlign={TextAlignment.center}
        appearance={TokenTextAppearance.headline400}
        bold
        id="compare-slider-description"
      >
        {t('body.details.gallery.compareSlider')}
      </StyledSubTitleWrapper>

      <SliderContainer
        data-testid="image-comparison-slider-container"
        aria-labelledby="compare-slider-description"
        aria-label={t('body.details.gallery.compareSliderAriaLabel')}
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <SliderImage
          data-testid="image-comparison-night-image"
          src={assetNight}
          draggable={false}
          alt={t('body.details.gallery.compareSliderNightImageAlt')}
        />

        <BeforeImageClip $position={position}>
          <SliderImage
            data-testid="image-comparison-day-image"
            src={assetDay}
            draggable={false}
            alt={t('body.details.gallery.compareSliderDayImageAlt')}
          />
        </BeforeImageClip>

        <HandleLine data-testid="image-comparison-handle-line" $position={position} />

        <HandleKnob
          data-testid="image-comparison-handle-knob"
          $position={position}
          role="slider"
          tabIndex={0}
          aria-label={t('body.details.gallery.compareSliderHandleAriaLabel')}
          aria-valuemin={MIN_SLIDER_POSITION}
          aria-valuemax={MAX_SLIDER_POSITION}
          aria-valuenow={Math.round(position)}
          onKeyDown={handleKnobKeyDown}
        >
          <Size variant="default" aria-hidden="true" />
        </HandleKnob>
      </SliderContainer>
    </StyledSliderWrapper>
  );
}
